import React, { createContext, useState, useEffect, useRef } from 'react';
import { setAuthContext } from '../utils/apiClient';

export const AuthContext = createContext({
  token: null,
  user: null,
  login: () => {},
  logout: () => {},
  setToken: () => {},
  loading: true,
  error: null,
});

// Helper to get token from URL params
function getTokenFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
  } catch (e) {
    return null;
  }
}

export function AuthProvider({ children }) {
  // Initialize token from URL first, then localStorage
  const [token, _setToken] = useState(() => {
    try {
      const urlToken = getTokenFromUrl();
      if (urlToken) {
        console.log('[AuthContext] Found token in URL');
        localStorage.setItem('token', urlToken);
        return urlToken;
      }
      return localStorage.getItem('token');
    } catch (error) {
      return null;
    }
  });

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Clean up URL after capturing token
  useEffect(() => {
    const urlToken = getTokenFromUrl();
    if (urlToken && token === urlToken) {
      const params = new URLSearchParams(window.location.search);
      params.delete('token');
      const newSearch = params.toString();
      const cleanedUrl =
        window.location.pathname +
        (newSearch ? `?${newSearch}` : '') +
        window.location.hash;
      window.history.replaceState({}, '', cleanedUrl);
    }
  }, [token]);

  // Log token changes after mount
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    console.log(`[AuthContext] Token ${token ? 'updated' : 'cleared'}`);
  }, [token]);

  // ============================================
  // FIX: Use lightweight /api/user/profile instead of subscription-status
  // This avoids duplicate subscription calls since SubscriptionContext handles that
  // ============================================
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Use profile endpoint for token validation (lighter than subscription-status)
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error('Token invalid');
        }

        const userData = await response.json();
        setUser(userData);
        setError(null);
        
      } catch (err) {
        console.warn(`[AuthContext] Token validation failed: ${err.message}`);
        localStorage.removeItem('token');
        _setToken(null);
        setUser(null);
        setError('Session expired. Please log in again.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Connect this context to the API client
  useEffect(() => {
    setAuthContext({
      logout: () => logout(),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setToken = (tok) => {
    try {
      if (tok) {
        localStorage.setItem('token', tok);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      // Ignore localStorage errors
    }
    _setToken(tok);
    setError(null);
  };

  const login = setToken;

  const logout = (reason = 'manual') => {
    console.log(`[AuthContext] logout: ${reason}`);
    localStorage.removeItem('token');
    _setToken(null);
    setUser(null);
    
    if (reason === 'expired') {
      setError('Your session has expired. Please log in again.');
    } else {
      setError(null);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      token, 
      user, 
      login, 
      logout, 
      setToken, 
      loading, 
      error,
      clearError: () => setError(null)
    }}>
      {children}
    </AuthContext.Provider>
  );
}